import { useContext, useEffect, useReducer } from 'react'
import {
    FormControlLabel,
    Switch,
    TextField,
    IconButton,
    Box,
    Tooltip,
    Chip,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
} from '@mui/material'
import { styled } from '@mui/material/styles'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
    calendarDateFromStored,
    calendarDateToStored,
    dateValidator,
} from '../../../../../utilities/functionTools'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { ToastMessage } from '../../../../../context/ToastPopUpContext'
import { Authentication } from '../../../../../context/AuthenticationContext'
import { getManufacturers } from '../../../../../apiRoutes'
import { parseFormWithFile } from '../../../../../utilities/functionTools'
import {
    FormSection,
    SaveButton,
    Grid,
    Autocomplete,
} from '../../../../UniversalComponents'
import {
    HelpOutline as HelpOutlineIcon,
    RemoveCircle as RemoveCircleIcon,
    DriveFolderUpload as DriveFolderUploadIcon,
    FiberManualRecord as FiberManualRecordIcon,
    CloudDone as CloudDoneIcon,
    FilePresent as FilePresentIcon,
    AttachFile as AttachFileIcon,
} from '@mui/icons-material'
import {
    basicValidator,
    cleanChecksumInput,
    normalizeChecksum,
} from '../../../../../utilities/functionTools'

interface engineCalibrationForm {
    row: any
    onClose: any
    quickStart: any
    addCalibrationFile: any
    permissionCheck: any
    calibrationData: any
}

const EngineCalibrationForm = ({
    row,
    onClose,
    quickStart,
    addCalibrationFile,
    permissionCheck,
    calibrationData,
}: engineCalibrationForm) => {
    const setToastMessage = useContext(ToastMessage)
    const currentUser = useContext(Authentication)
    const { username, userProfile } = currentUser.auth
    const { superAdmin } = userProfile

    const defaultState = {
        formData: {
            id: row?.id ? row?.id : `${Math.random()}`,
            engineCalibrationECMPartNumber:
                row?.engineCalibrationECMPartNumber || '',
            engineCalibrationFileUrl: row?.engineCalibrationFileUrl || '',
            engineCalibrationFileName: row?.engineCalibrationFileName || '',
            engineCalibrationProductionDate:
                row?.engineCalibrationProductionDate
                    ? calendarDateFromStored(
                          row?.engineCalibrationProductionDate
                      )
                    : null,
            engineCalibrationRevision: row?.engineCalibrationRevision || '',
            engineCalibrationEControlsSoftwareRevision:
                row?.engineCalibrationEControlsSoftwareRevision || '',
            engineCalibrationSoftwareModel:
                row?.engineCalibrationSoftwareModel || '',
            engineCalibrationChecksum: row?.engineCalibrationChecksum || '',
            engineCalibrationReleaseNotes:
                row?.engineCalibrationReleaseNotes || '',
            engineCalibrationApproved: row?.engineCalibrationApproved || false,
            engineCalibrationActive: row?.engineCalibrationActive || false,
            engineCalibrationSeverityLevel:
                row?.engineCalibrationSeverityLevel || '',
            engineCalibrationMD5: row?.engineCalibrationMD5 || '',
        },

        formValidation: {
            engineCalibrationChecksumValidation: false,
            engineCalibrationSeverityLevelValidation: false,
            engineCalibrationProductionDateValidation: false,
            engineCalibrationFileNameValidation: false,
            isFormDirty: false,
            isFormValid: false,
        },

        selectedFile: {},
        uploading: false,
        setVersionNameInput: '',
        closeModal: false,
        calibrationFileNameDisabled: row.calibrationFileName ? true : false,
        manufacturerChoiceDisabled: row.manufacturerName ? true : false,
        manufacturerOptions: [],
        manufacturers: [],
        shortChecksum:
            row?.engineCalibrationChecksum?.length === 4 ? true : false,
        checksumCharacterCount: '',
        open: false,
    }

    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)

    const {
        formData,
        selectedFile,
        formValidation,
        manufacturers,
        shortChecksum,
        checksumCharacterCount,
        open,
        uploading,
    } = state

    const {
        engineCalibrationECMPartNumber,
        engineCalibrationFileUrl,
        engineCalibrationFileName,
        engineCalibrationProductionDate,
        engineCalibrationRevision,
        engineCalibrationEControlsSoftwareRevision,
        engineCalibrationSoftwareModel,
        engineCalibrationChecksum,
        engineCalibrationReleaseNotes,
        engineCalibrationApproved,
        engineCalibrationActive,
        engineCalibrationSeverityLevel,
        engineCalibrationMD5,
    } = formData

    const {
        engineCalibrationChecksumValidation,
        engineCalibrationProductionDateValidation,
        engineCalibrationSeverityLevelValidation,
        engineCalibrationFileNameValidation,
        isFormDirty,
        isFormValid,
    } = formValidation

    // What would be stored for the checksum (null when it is not valid yet),
    // and every required field that still blocks Save & Close. The list is
    // shown above the button so the user is never left with a disabled
    // button and no explanation.
    const checksumToSave = normalizeChecksum(
        engineCalibrationChecksum,
        shortChecksum
    )
    const productionDateIsValid =
        dateValidator(engineCalibrationProductionDate) !== null
    const missingFields: string[] = []
    if (!engineCalibrationFileName) {
        missingFields.push('calibration file (.emot upload)')
    }
    if (!productionDateIsValid) {
        missingFields.push('production date')
    }
    if (!engineCalibrationSeverityLevel) {
        missingFields.push('severity level')
    }
    if (!checksumToSave) {
        missingFields.push(
            !engineCalibrationChecksum
                ? 'checksum'
                : shortChecksum
                ? 'checksum (4 hex characters)'
                : 'checksum (8 hex characters)'
        )
    }

    const getEngineManufacturerData = async () => {
        try {
            const data = await getManufacturers(true)
            if (data && data.length !== 0) {
                setState({
                    manufacturerOptions: data
                        .filter(
                            (manufacturer) => manufacturer.type === 'engine'
                        )
                        .map(({ manufacturerName }) => manufacturerName),
                    manufacturers: data.filter(
                        (manufacturer) => manufacturer.type === 'engine'
                    ),
                })
            }
        } catch (err: any) {
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const handleSubmit = async () => {
        // A new .emot was chosen in this dialog (vs. editing details only).
        const hasNewFile = !!selectedFile?.name

        // Block the same file name on a *different* calibration of this
        // engine. Re-uploading a file onto the calibration being edited is
        // allowed: it replaces that calibration's file (previously impossible,
        // the only way out was deleting the engine and re-uploading all cals).
        const findDuplicate = hasNewFile
            ? calibrationData.filter(
                  (file) =>
                      file.engineCalibrationFileName === selectedFile.name &&
                      (file._id ?? file.id) !== formData.id
              )
            : []

        if (findDuplicate.length !== 0) {
            setToastMessage(
                `${selectedFile.name} is already used by another calibration of this engine. Choose a unique file, or edit that calibration to replace its file.`,
                'error'
            )
            return
        }

        delete formData.updatedAt
        const calibrationFile = [selectedFile]
        const newFormData = {
            ...formData,
            // Store the cleaned value: no "$", no spaces, leading zero
            // restored. Save & Close is only enabled when it is valid.
            engineCalibrationChecksum: checksumToSave,
            // Stored as UTC midnight of the chosen day (no time-zone shift).
            engineCalibrationProductionDate: calendarDateToStored(
                engineCalibrationProductionDate
            ),
            updatedBy: username,
        }

        const parsedFormData = parseFormWithFile(newFormData, calibrationFile)

        // Wait for the upload before closing. Closing straight away let the
        // engine be saved while the file was still uploading, which stored
        // calibrations with no download link or MD5. On failure the dialog
        // stays open (the error is shown as a toast) so it can be retried.
        setState({ uploading: true })
        const saved = await addCalibrationFile(
            newFormData,
            parsedFormData,
            hasNewFile
        )
        setState({ uploading: false })

        if (saved) {
            onClose(newFormData)
        }
    }

    const handleChange = (e: any) => {
        const { name, value } = e.target
        if (name === 'manufacturerName') {
            const newEngineManufacturerId = manufacturers.find(
                (file) => file.manufacturerName === value
            )

            setState({
                formData: {
                    ...formData,
                    manufacturer: newEngineManufacturerId
                        ? newEngineManufacturerId._id
                        : '',
                },
            })
        } else if (name === 'engineCalibrationChecksum') {
            // "$813EE0A" or " 0813ee0a " both become "813EE0A" / "0813EE0A".
            const cleaned = cleanChecksumInput(value)
            setState({
                checksumCharacterCount: cleaned,
                formData: { ...formData, [name]: cleaned },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
            })
        } else {
            setState({
                formData: { ...formData, [name]: value },
                formValidation: {
                    ...formValidation,
                    isFormDirty: true,
                },
            })
        }
    }

    const handleFileSelect = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0]

            setState({
                selectedFile: file,
                formValidation: { ...formValidation, isFormDirty: true },
                formData: {
                    ...formData,
                    engineCalibrationFileName: file?.name,
                    engineCalibrationFileUrl: '',
                },
            })
        }
    }

    const handleFileRemove = () => {
        setState({
            selectedFile: [],
        })
    }

    const handleReset = () => {
        setState({
            formData: defaultState.formData,
            formValidation: defaultState.formValidation,
            selectedFile: '',
            checksumCharacterCount: defaultState.checksumCharacterCount,
            shortChecksum: defaultState.shortChecksum,
        })
    }

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    })

    useEffect(() => {
        getEngineManufacturerData()
    }, [])

    // Save & Close is enabled when something changed and nothing required is
    // missing (see missingFields above). Previously a 7-digit checksum or a
    // typed date silently kept the button disabled.
    const canSave =
        isFormDirty &&
        missingFields.length === 0 &&
        !engineCalibrationFileNameValidation &&
        !engineCalibrationSeverityLevelValidation &&
        !engineCalibrationChecksumValidation &&
        !engineCalibrationProductionDateValidation

    useEffect(() => {
        if (canSave !== isFormValid) {
            setState({
                formValidation: {
                    ...formValidation,
                    isFormValid: canSave,
                },
            })
        }
    }, [canSave, isFormValid])

    const severityOptions = [
        '0 - Nonspecific',
        '1 - Suggested',
        '2 - Recommended',
        '3 - Required',
    ]

    return (
        <Grid container spacing={2} sx={{ marginTop: 5 }}>
            <Grid item xs={12} md={7}>
                <FormSection title="Engine Calibration File ">
                    <Grid item xs={12}>
                        <TextField
                            name="engineCalibrationFileName"
                            label="EMOT File Name"
                            value={engineCalibrationFileName}
                            fullWidth
                            onChange={handleChange}
                            disabled={true}
                        />
                    </Grid>
                    {engineCalibrationMD5 && (
                        <Grid item xs={12}>
                            <TextField
                                name="engineCalibrationMD5"
                                label="Content MD5"
                                value={engineCalibrationMD5}
                                fullWidth
                                onChange={handleChange}
                                disabled={true}
                                sx={{
                                    display:
                                        superAdmin === true ? null : 'none',
                                }}
                            />
                        </Grid>
                    )}
                </FormSection>
            </Grid>
            <Grid item xs={12} md={5}>
                <FormSection
                    title="Engine Calibration File Upload"
                    reset={
                        <Button
                            onClick={() => setState({ open: true })}
                            disabled={permissionCheck}
                        >
                            <HelpOutlineIcon />
                        </Button>
                    }
                >
                    <Grid item xs={12} sx={{ paddingBottom: 0.7 }}>
                        <label htmlFor="raised-button-file">
                            <Tooltip
                                title={
                                    engineCalibrationFileUrl ? (
                                        <h3 style={{ color: 'primary' }}>
                                            Replace Calibration File
                                        </h3>
                                    ) : (
                                        <h3 style={{ color: 'inherit' }}>
                                            Upload Calibration File
                                        </h3>
                                    )
                                }
                            >
                                <center>
                                    {engineCalibrationFileUrl ? (
                                        <Button
                                            component="label"
                                            disabled={permissionCheck}
                                        >
                                            <Box>
                                                <FilePresentIcon
                                                    sx={{
                                                        fontSize: 90,
                                                    }}
                                                />
                                                <CloudDoneIcon
                                                    sx={{
                                                        fontSize: 90,
                                                    }}
                                                />
                                                <VisuallyHiddenInput
                                                    onChange={handleFileSelect}
                                                    type="file"
                                                    accept=".emot"
                                                    id='htmlFor="raised-button-file'
                                                />
                                            </Box>
                                        </Button>
                                    ) : (
                                        <IconButton
                                            component="label"
                                            disabled={permissionCheck}
                                        >
                                            <DriveFolderUploadIcon
                                                sx={{
                                                    fontSize: 90,
                                                }}
                                            />
                                            <VisuallyHiddenInput
                                                onChange={handleFileSelect}
                                                type="file"
                                                accept=".emot"
                                                id='htmlFor="raised-button-file'
                                            />
                                        </IconButton>
                                    )}
                                </center>
                            </Tooltip>
                        </label>
                    </Grid>
                    <Grid item xs={12}>
                        {selectedFile?.name && (
                            <Chip
                                label={selectedFile?.name}
                                variant="outlined"
                                color="info"
                                icon={
                                    <AttachFileIcon
                                        sx={{
                                            color: 'primary',
                                        }}
                                    />
                                }
                                onDelete={() => {
                                    handleFileRemove()
                                    setState({
                                        formData: {
                                            ...formData,
                                            engineCalibrationFileUrl:
                                                defaultState.formData
                                                    .engineCalibrationFileUrl,
                                            engineCalibrationFileName:
                                                defaultState.formData
                                                    .engineCalibrationFileName,
                                        },
                                        formValidation: {
                                            ...formValidation,
                                            isFormDirty: true,
                                        },
                                    })
                                }}
                                sx={{
                                    color: 'grey',
                                    fontSize: 25,
                                    borderRadius: 0,
                                }}
                                deleteIcon={<RemoveCircleIcon />}
                            />
                        )}
                    </Grid>
                </FormSection>
            </Grid>

            <Grid item xs={12} sx={{ marginTop: -2 }}>
                <FormSection title="Deployment & Production">
                    <Grid item xs={12} md={2}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Production Date"
                                views={['year', 'month', 'day']}
                                value={engineCalibrationProductionDate}
                                // onChange fires both when a day is picked in
                                // the calendar and when a date is typed into
                                // the field. Typed dates used to be ignored
                                // (only onAccept was wired), which left Save &
                                // Close disabled. An incomplete typed date is
                                // kept as-is so the field does not reset; it
                                // only counts once it is a valid date.
                                onChange={(value) => {
                                    setState({
                                        formData: {
                                            ...formData,
                                            engineCalibrationProductionDate:
                                                value,
                                        },
                                        formValidation: {
                                            ...formValidation,
                                            isFormDirty: true,
                                        },
                                    })
                                }}
                                sx={{ width: '100%' }}
                                disabled={permissionCheck}
                                slotProps={{
                                    textField: {
                                        required: true,
                                        helperText:
                                            isFormDirty &&
                                            !productionDateIsValid
                                                ? engineCalibrationProductionDate
                                                    ? 'Enter a complete date'
                                                    : 'Production Date is required'
                                                : '',
                                        error:
                                            (isFormDirty ||
                                                engineCalibrationProductionDateValidation) &&
                                            !productionDateIsValid,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Autocomplete
                            label="Severity Level"
                            required={true}
                            handleChange={handleChange}
                            options={severityOptions}
                            name="engineCalibrationSeverityLevel"
                            value={engineCalibrationSeverityLevel}
                            onBlur={(e: any) =>
                                basicValidator(e, formValidation, setState)
                            }
                            helperText={
                                engineCalibrationSeverityLevelValidation &&
                                !engineCalibrationSeverityLevel
                                    ? 'Severity Level is required'
                                    : ''
                            }
                            error={
                                engineCalibrationSeverityLevelValidation &&
                                !engineCalibrationSeverityLevel
                            }
                            disabled={permissionCheck}
                        />
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={9}>
                                <TextField
                                    name="engineCalibrationChecksum"
                                    required={true}
                                    label="Checksum"
                                    value={engineCalibrationChecksum}
                                    fullWidth
                                    onChange={handleChange}
                                    slotProps={{
                                        htmlInput: {
                                            // Room for a pasted "$" / "0x" and
                                            // spaces, which are cleaned off.
                                            // A hard 8 used to cut "$EE60BA6D"
                                            // down to "$EE60BA6" on paste.
                                            maxLength: 16,
                                        },
                                    }}
                                    onBlur={(e: any) =>
                                        basicValidator(
                                            e,
                                            formValidation,
                                            setState
                                        )
                                    }
                                    disabled={permissionCheck}
                                    helperText={
                                        engineCalibrationChecksum &&
                                        !checksumToSave
                                            ? shortChecksum
                                                ? 'Enter 4 hex characters (0-9, A-F)'
                                                : 'Enter the 8 hex characters (0-9, A-F) the ECM reports'
                                            : checksumToSave &&
                                              checksumToSave !==
                                                  engineCalibrationChecksum
                                            ? `Leading zero added: will be saved as ${checksumToSave}`
                                            : engineCalibrationChecksumValidation &&
                                              !engineCalibrationChecksum
                                            ? 'Checksum is Required.'
                                            : ''
                                    }
                                    error={
                                        (!!engineCalibrationChecksum &&
                                            !checksumToSave) ||
                                        (engineCalibrationChecksumValidation &&
                                            !engineCalibrationChecksum)
                                    }
                                />
                                <Box>
                                    <Typography
                                        sx={{
                                            marginTop: 0.5,

                                            display: checksumCharacterCount
                                                ? ''
                                                : 'none',
                                        }}
                                        fontSize={'small'}
                                        color={
                                            checksumToSave
                                                ? 'primary'
                                                : '#AEAEAE'
                                        }
                                    >
                                        {shortChecksum
                                            ? `${
                                                  checksumCharacterCount.length
                                              } / ${4} Characters`
                                            : `${
                                                  checksumCharacterCount.length
                                              } / ${8} Characters`}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            onChange={() => {
                                                const { shortChecksum } = state
                                                const {
                                                    engineCalibrationChecksum,
                                                } = formData

                                                let updatedChecksum =
                                                    engineCalibrationChecksum.substring(
                                                        0,
                                                        4
                                                    )

                                                setState({
                                                    shortChecksum:
                                                        !shortChecksum,
                                                    formData: {
                                                        ...formData,
                                                        engineCalibrationChecksum:
                                                            updatedChecksum,
                                                    },
                                                    formValidation: {
                                                        ...formValidation,
                                                        isFormDirty: true,
                                                    },
                                                    checksumCharacterCount:
                                                        updatedChecksum,
                                                })
                                            }}
                                            slotProps={{
                                                input: {
                                                    'aria-label': 'controlled',
                                                },
                                            }}
                                            id="switch.shortChecksum"
                                            name="switch.shortChecksum"
                                            checked={shortChecksum}
                                            key="switch.shortChecksum"
                                            disabled={permissionCheck}
                                        />
                                    }
                                    label="Short Checksum"
                                    key="switch.shortChecksum"
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            name="engineCalibrationECMPartNumber"
                            label="ECM Part Number"
                            value={engineCalibrationECMPartNumber}
                            fullWidth
                            disabled={permissionCheck}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3.5}>
                        <TextField
                            name="engineCalibrationSoftwareModel"
                            label="Software Model"
                            value={engineCalibrationSoftwareModel}
                            fullWidth
                            disabled={permissionCheck}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            name="engineCalibrationRevision"
                            label="Rev"
                            value={engineCalibrationRevision}
                            fullWidth
                            disabled={permissionCheck}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={2.5}>
                        <TextField
                            name="engineCalibrationEControlsSoftwareRevision"
                            label="Econtrols Software Revision"
                            value={engineCalibrationEControlsSoftwareRevision}
                            fullWidth
                            disabled={permissionCheck}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    onChange={() =>
                                        setState({
                                            formData: {
                                                ...formData,
                                                engineCalibrationActive:
                                                    !engineCalibrationActive,
                                            },
                                            formValidation: {
                                                ...formValidation,
                                                isFormDirty: true,
                                            },
                                        })
                                    }
                                    slotProps={{
                                        input: { 'aria-label': 'controlled' },
                                    }}
                                    id="switch.engineCalibrationActive"
                                    name="switch.engineCalibrationActive"
                                    checked={engineCalibrationActive}
                                    key="switch.engineCalibrationActive"
                                />
                            }
                            label="Engine Calibration Active"
                            key="switch.engineCalibrationActive"
                            disabled={
                                engineCalibrationActive || permissionCheck
                            }
                        />
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <TextField
                            name="engineCalibrationReleaseNotes"
                            label="Release Notes"
                            value={engineCalibrationReleaseNotes}
                            fullWidth
                            disabled={permissionCheck}
                            onChange={handleChange}
                        />
                    </Grid>
                </FormSection>

                <Dialog
                    open={open}
                    onClose={() => setState({ open: false })}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle align="center">
                        Calibration File Upload
                    </DialogTitle>
                    <DialogContent>
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                <Box display="flex" alignItems="center">
                                    <FiberManualRecordIcon sx={{ mr: 1 }} />
                                    Upload a Calibration File by clicking on the
                                    icon.
                                </Box>
                            </Typography>

                            <Typography
                                variant="body1"
                                gutterBottom
                                sx={{ marginTop: 2 }}
                            >
                                <Box display="flex" alignItems="center">
                                    <FiberManualRecordIcon sx={{ mr: 1 }} />
                                    <span>
                                        A Calibration File is{' '}
                                        <strong>required</strong> for the form
                                        to submit.
                                    </span>
                                </Box>
                            </Typography>
                        </Box>
                    </DialogContent>
                </Dialog>

                <Grid item xs={12} md={12} sx={{ marginTop: 5 }}>
                    {isFormDirty && missingFields.length !== 0 && (
                        <Typography
                            align="center"
                            color="warning.main"
                            fontSize="small"
                            sx={{ marginBottom: 1 }}
                        >
                            To save, still needed: {missingFields.join(', ')}
                        </Typography>
                    )}
                    {uploading && (
                        <Typography
                            align="center"
                            color="primary"
                            fontSize="small"
                            sx={{ marginBottom: 1 }}
                        >
                            Uploading calibration file, please wait…
                        </Typography>
                    )}
                    <SaveButton
                        title="Required Fields Are Marked With An *"
                        titleAlignment="center"
                        handleSubmit={handleSubmit}
                        handleReset={handleReset}
                        disabledSave={
                            !isFormValid || permissionCheck || uploading
                        }
                        disabledReset={
                            !isFormDirty || permissionCheck || uploading
                        }
                        quickStart={quickStart}
                        resetVariant="contained"
                    />
                </Grid>
            </Grid>
        </Grid>
    )
}

export default EngineCalibrationForm
